import { v4 as uuidv4 } from 'uuid';

/**
 * IndexedDB Service for offline data storage
 */
class IndexedDBService {
  private readonly DB_NAME = 'accounts_offline_db';
  private readonly DB_VERSION = 5;
  private db: IDBDatabase | null = null;
  private initializationInProgress: boolean = false;
  private initCallbacks: Array<(success: boolean) => void> = [];
  private _initProgress: number = 0;
  private _initError: string | null = null;
  // Listeners for data changes
  private listeners: Array<() => void> = [];

  // Store names
  private readonly INVOICES_STORE = 'invoices';
  private readonly CLIENTS_STORE = 'clients';
  private readonly INVENTORY_STORE = 'inventory';
  private readonly BUSINESS_STORE = 'business';
  private readonly COUNTER_STORE = 'counters';
  private readonly USER_SETTINGS_STORE = 'userSettings';
  private readonly INVOICE_TEMPLATES_STORE = 'invoiceTemplates';
  private readonly PURCHASES_STORE = 'purchases';
  private readonly VENDORS_STORE = 'vendors';
  private readonly USER_PROFILES_STORE = 'user_profiles';

  // Cache keys
  private readonly CACHE_PREFIX = 'akmo_cache_';
  private readonly CACHE_INVENTORY = this.CACHE_PREFIX + 'inventory';
  private readonly CACHE_CLIENTS = this.CACHE_PREFIX + 'clients';
  private readonly CACHE_BUSINESS = this.CACHE_PREFIX + 'business';
  private readonly CACHE_COUNTERS = this.CACHE_PREFIX + 'counters';
  private readonly CACHE_INVOICES = this.CACHE_PREFIX + 'invoices';

  constructor() {
    // Try to open IndexedDB during class instantiation
    this.tryInitDB();
  }

  /**
   * Get the current initialization progress (0-100)
   */
  public get initProgress(): number {
    return this._initProgress;
  }

  /**
   * Get any initialization error
   */
  public get initError(): string | null {
    return this._initError;
  }

  /**
   * Add a listener for data changes
   * @param listener Callback function
   * @returns Unsubscribe function
   */
  public addListener(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of data changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('Error in data change listener:', error);
      }
    });
  }

  /**
   * Check if the database is initialized
   * @returns True if the database is initialized, false otherwise
   */
  public isInitialized(): boolean {
    return this.db !== null;
  }

  /**
   * Try to initialize the database automatically
   * This method is called during class instantiation
   */
  private tryInitDB(): void {
    this.initDB().catch(error => {
      console.error('Initial IndexedDB initialization failed:', error);
      // Will fall back to cache when methods are called
    });
  }

  /**
   * Initialize the database with progress tracking
   * @returns Promise that resolves to true if the database was initialized successfully
   */
  public async initDB(): Promise<boolean> {
    // If already initialized, return immediately
    if (this.db) {
      this._initProgress = 100;
      return Promise.resolve(true);
    }

    // If initialization is already in progress, return a promise that resolves when it completes
    if (this.initializationInProgress) {
      return new Promise<boolean>((resolve) => {
        this.initCallbacks.push(resolve);
      });
    }

    this.initializationInProgress = true;
    this._initProgress = 0;
    this._initError = null;

    return new Promise((resolve, reject) => {
      try {
        this._initProgress = 10;
        const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

        request.onerror = (event) => {
          const error = 'Error opening IndexedDB: ' + (event.target as IDBOpenDBRequest).error;
          console.error(error, event);
          this._initError = error;
          this._initProgress = 0;
          this.initializationInProgress = false;

          // Resolve all pending callbacks
          this.initCallbacks.forEach(callback => callback(false));
          this.initCallbacks = [];

          reject(false);
        };

        request.onsuccess = (event) => {
          this.db = (event.target as IDBOpenDBRequest).result;
          this._initProgress = 100;
          this.initializationInProgress = false;

          // Set up error handling for the database connection
          this.db.onerror = (event) => {
            console.error('IndexedDB error:', event);
          };

          // Add a handler to detect when connection is closed or lost
          this.db.onclose = () => {
            console.warn('IndexedDB connection closed unexpectedly');
            this.db = null;
            this._initProgress = 0;
            this.tryInitDB(); // Try to reinitialize
          };

          // Verify all object stores exist
          this.verifyStores();

          // Migration from cache if necessary
          this.migrateFromCache();

          // Resolve all pending callbacks
          this.initCallbacks.forEach(callback => callback(true));
          this.initCallbacks = [];

          resolve(true);
        };

        request.onupgradeneeded = (event) => {
          this._initProgress = 50;
          const db = (event.target as IDBOpenDBRequest).result;

          // Create stores if they don't exist
          if (!db.objectStoreNames.contains(this.INVOICES_STORE)) {
            db.createObjectStore(this.INVOICES_STORE, { keyPath: 'id' });
          }

          if (!db.objectStoreNames.contains(this.CLIENTS_STORE)) {
            db.createObjectStore(this.CLIENTS_STORE, { keyPath: 'id' });
          }

          if (!db.objectStoreNames.contains(this.INVENTORY_STORE)) {
            db.createObjectStore(this.INVENTORY_STORE, { keyPath: 'id' });
          }

          if (!db.objectStoreNames.contains(this.BUSINESS_STORE)) {
            db.createObjectStore(this.BUSINESS_STORE, { keyPath: 'id' });
          }

          if (!db.objectStoreNames.contains(this.COUNTER_STORE)) {
            const counterStore = db.createObjectStore(this.COUNTER_STORE, { keyPath: 'id' });
            counterStore.createIndex('userId', 'userId', { unique: false });
            counterStore.createIndex('type', 'type', { unique: false });
          }

          // Version 2 stores
          if (!db.objectStoreNames.contains('userSettings')) {
            db.createObjectStore('userSettings', { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains('invoiceTemplates')) {
            db.createObjectStore('invoiceTemplates', { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains('purchases')) {
            db.createObjectStore('purchases', { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains('vendors')) {
            db.createObjectStore('vendors', { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains('user_profiles')) {
            db.createObjectStore('user_profiles', { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains('cashTransactions')) {
            db.createObjectStore('cashTransactions', { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains('bankTransactions')) {
            db.createObjectStore('bankTransactions', { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains('expenses')) {
            db.createObjectStore('expenses', { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains('vendorBills')) {
            db.createObjectStore('vendorBills', { keyPath: 'id' });
          }

          this._initProgress = 75;
        };
      } catch (error) {
        this._initError = 'Failed to open IndexedDB: ' + error;
        this._initProgress = 0;
        this.initializationInProgress = false;
        console.error(this._initError);
        reject(false);
      }
    });
  }

  /**
   * Verify that all required object stores exist
   */
  private verifyStores(): void {
    if (!this.db) return;

    const requiredStores = [
      this.INVOICES_STORE,
      this.CLIENTS_STORE,
      this.INVENTORY_STORE,
      this.BUSINESS_STORE,
      this.BUSINESS_STORE,
      this.COUNTER_STORE,
      'userSettings',
      'invoiceTemplates',
      'purchases',
      'vendors',
      'user_profiles'
    ];

    const missingStores = requiredStores.filter(
      store => !this.db?.objectStoreNames.contains(store)
    );

    if (missingStores.length > 0) {
      console.warn('Missing IndexedDB object stores: ' + missingStores.join(', '));
      this._initError = 'Missing stores: ' + missingStores.join(', ');
      // We don't reset the DB to null here, but record the warning
    }
  }

  /**
   * Migrate data from cache to IndexedDB if necessary
   */
  private async migrateFromCache(): Promise<void> {
    try {
      // Check if we have cached data
      const cachedInventory = this.getFromCache<any[]>(this.CACHE_INVENTORY);
      const cachedClients = this.getFromCache<any[]>(this.CACHE_CLIENTS);
      const cachedBusiness = this.getFromCache<any[]>(this.CACHE_BUSINESS);
      const cachedInvoices = this.getFromCache<any[]>(this.CACHE_INVOICES);

      // Migrate inventory items
      if (cachedInventory && cachedInventory.length > 0) {
        for (const item of cachedInventory) {
          try {
            await this.saveInventoryItem(item);
          } catch (error) {
            console.error(`Failed to migrate inventory item ${item.id}:`, error);
          }
        }
      }

      // Migrate clients
      if (cachedClients && cachedClients.length > 0) {
        for (const client of cachedClients) {
          try {
            await this.saveClient(client);
          } catch (error) {
            console.error(`Failed to migrate client ${client.id}:`, error);
          }
        }
      }

      // Migrate business details
      if (cachedBusiness && cachedBusiness.length > 0) {
        for (const business of cachedBusiness) {
          try {
            await this.saveBusinessDetails(business);
          } catch (error) {
            console.error(`Failed to migrate business ${business.id}:`, error);
          }
        }
      }

      // Migrate invoices
      if (cachedInvoices && cachedInvoices.length > 0) {
        for (const invoice of cachedInvoices) {
          try {
            await this.saveInvoice(invoice);
          } catch (error) {
            console.error(`Failed to migrate invoice ${invoice.id}:`, error);
          }
        }
      }
    } catch (error) {
      console.error('Error during cache migration:', error);
    }
  }

  /**
   * Store data in local storage cache
   * @param key Cache key
   * @param data Data to cache
   */
  private saveToCache<T>(key: string, data: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error(`Error saving to cache (${key}):`, error);
    }
  }

  /**
   * Get data from local storage cache
   * @param key Cache key
   * @returns Cached data or null if not found
   */
  private getFromCache<T>(key: string): T | null {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) as T : null;
    } catch (error) {
      console.error(`Error getting from cache (${key}):`, error);
      return null;
    }
  }

  /**
   * Add item to cached array
   * @param key Cache key
   * @param item Item to add to the array
   */
  private addToArrayCache<T extends object>(key: string, item: T): void {
    try {
      const existingData = this.getFromCache<T[]>(key) || [];

      // If item has an id, update it if it exists, otherwise add it
      if ('id' in item) {
        const itemId = (item as any).id;
        const index = existingData.findIndex((i: any) => i.id === itemId);

        if (index >= 0) {
          existingData[index] = item;
        } else {
          existingData.push(item);
        }
      } else {
        existingData.push(item);
      }

      this.saveToCache(key, existingData);
    } catch (error) {
      console.error(`Error adding to array cache (${key}):`, error);
    }
  }

  /**
   * Generate an invoice number based on business name and maintaining a counter
   * @param userId User ID
   * @param businessName Business name to generate prefix
   * @returns Generated invoice number
   */
  public async generateInvoiceNumber(userId: string, businessName: string): Promise<string> {
    try {
      // Ensure database is initialized
      if (!this.db) {
        const initialized = await this.initDB();
        if (!initialized) {
          // Fall back to a simple counter in cache
          return this.generateInvoiceNumberFromCache(userId, businessName);
        }
      }

      // Get the current financial year
      const today = new Date();
      const fiscalYearStart = new Date(today.getFullYear(), 3, 1); // April 1st
      let financialYear: string;

      // If current date is before April 1st, use previous year
      if (today < fiscalYearStart) {
        financialYear = (today.getFullYear() - 1) + '-' + today.getFullYear().toString().slice(-2);
      } else {
        financialYear = today.getFullYear() + '-' + (today.getFullYear() + 1).toString().slice(-2);
      }

      // Generate prefix from business name (first 3 letters)
      let prefix = businessName ? businessName.substring(0, 3).toUpperCase() : 'INV';
      if (!prefix || prefix.trim() === '') {
        prefix = 'INV';
      }

      // Get current counter for this user and financial year
      const counterId = 'invoice_' + userId + '_' + financialYear;
      const currentCounter = await this.getCounter(counterId);
      const newCounter = currentCounter + 1;

      // Format the serial number part with padding zeros
      const serialNumber = String(newCounter).padStart(3, '0');

      // Update the counter
      await this.updateCounter(counterId, newCounter);

      // Format: PREFIX/YY-YY/001
      const invoiceNumber = prefix + '/' + financialYear + '/' + serialNumber;

      // Also cache this invoice number as fallback
      this.cacheInvoiceNumber(userId, invoiceNumber);

      return invoiceNumber;
    } catch (error) {
      console.error('Error generating invoice number from IndexedDB:', error);
      return this.generateInvoiceNumberFromCache(userId, businessName);
    }
  }

  /**
   * Generate invoice number from cache as fallback
   */
  private generateInvoiceNumberFromCache(userId: string, businessName: string): string {
    try {
      // Get the current financial year
      const today = new Date();
      const fiscalYearStart = new Date(today.getFullYear(), 3, 1); // April 1st
      let financialYear: string;

      // If current date is before April 1st, use previous year
      if (today < fiscalYearStart) {
        financialYear = (today.getFullYear() - 1) + '-' + today.getFullYear().toString().slice(-2);
      } else {
        financialYear = today.getFullYear() + '-' + (today.getFullYear() + 1).toString().slice(-2);
      }

      // Generate prefix from business name (first 3 letters)
      let prefix = businessName ? businessName.substring(0, 3).toUpperCase() : 'INV';
      if (!prefix || prefix.trim() === '') {
        prefix = 'INV';
      }

      // Use a cache counter
      const counterKey = this.CACHE_PREFIX + 'counter_' + userId + '_' + financialYear;
      let counter = 1;

      try {
        const cachedCounter = localStorage.getItem(counterKey);
        if (cachedCounter) {
          counter = parseInt(cachedCounter, 10) + 1;
        }
        localStorage.setItem(counterKey, counter.toString());
      } catch (error) {
        console.error('Error updating cached counter:', error);
      }

      // Format the serial number part with padding zeros
      const serialNumber = String(counter).padStart(3, '0');

      // Format: PREFIX/YY-YY/001
      const invoiceNumber = prefix + '/' + financialYear + '/' + serialNumber;
      this.cacheInvoiceNumber(userId, invoiceNumber);

      return invoiceNumber;
    } catch (error) {
      console.error('Error generating invoice number from cache:', error);
      return 'INV-' + Date.now().toString().slice(-4);
    }
  }

  /**
   * Cache the latest invoice number for fallback
   */
  private cacheInvoiceNumber(userId: string, invoiceNumber: string): void {
    try {
      localStorage.setItem(this.CACHE_PREFIX + 'latest_invoice_' + userId, invoiceNumber);
    } catch (error) {
      console.error('Error caching invoice number:', error);
    }
  }

  /**
   * Get the current counter value
   * @param counterId Counter ID
   * @returns Current counter value or 0 if not found
   */
  private async getCounter(counterId: string): Promise<number> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.COUNTER_STORE], 'readonly');
      const store = transaction.objectStore(this.COUNTER_STORE);
      const request = store.get(counterId);

      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result.value);
        } else {
          resolve(0); // Counter not found, start from 0
        }
      };

      request.onerror = (event) => {
        console.error('Error getting counter:', event);
        reject(new Error('Failed to get counter'));
      };
    });
  }

  /**
   * Update counter value
   * @param counterId Counter ID
   * @param value New counter value
   */
  private async updateCounter(counterId: string, value: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.COUNTER_STORE], 'readwrite');
      const store = transaction.objectStore(this.COUNTER_STORE);
      const request = store.put({
        id: counterId,
        value,
        updatedAt: new Date().toISOString()
      });

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = (event) => {
        console.error('Error updating counter:', event);
        reject(new Error('Failed to update counter'));
      };
    });
  }

  /**
   * Save an invoice with cache fallback
   */
  public async saveInvoice(invoice: any): Promise<any> {
    try {
      // Basic validation of invoice data
      if (!invoice || typeof invoice !== 'object') {
        console.error('Invalid invoice data provided');
        throw new Error('Invalid invoice data');
      }

      // Ensure we have an ID
      if (!invoice.id) {
        invoice.id = uuidv4();
      }

      // Add timestamp
      invoice.updatedAt = new Date().toISOString();
      if (!invoice.createdAt) {
        invoice.createdAt = invoice.updatedAt;
      }

      // Ensure required fields exist
      if (!invoice.status) {
        invoice.status = 'draft';
      }

      if (!invoice.syncStatus) {
        invoice.syncStatus = 'pending';
      }

      // Ensure client details exists
      if (!invoice.clientDetails) {
        invoice.clientDetails = {
          address: '',
          gstin: '',
          email: '',
          phone: '',
          state: ''
        };
      }

      // Ensure bank details exists
      if (!invoice.bankDetails) {
        invoice.bankDetails = {};
      }

      // Always cache the invoice, even if IndexedDB is available
      this.addToArrayCache(this.CACHE_INVOICES, invoice);

      // If IndexedDB is not available, return from cache
      if (!this.db) {
        const initialized = await this.initDB();
        if (!initialized) {
          console.log('IndexedDB not available, using cache for invoice');
          return invoice;
        }
      }

      // Continue with IndexedDB operation
      return new Promise((resolve, reject) => {
        if (!this.db) {
          reject(new Error('Database not initialized'));
          return;
        }

        const transaction = this.db.transaction([this.INVOICES_STORE], 'readwrite');
        const store = transaction.objectStore(this.INVOICES_STORE);
        const request = store.put(invoice);

        request.onsuccess = () => {
          this.notifyListeners();
          resolve(invoice);
        };

        request.onerror = (event) => {
          console.error('Error saving invoice:', event);
          reject(new Error('Failed to save invoice'));
        };
      });
    } catch (error) {
      console.error('Error in saveInvoice:', error);
      // Return the invoice anyway, since it was cached
      return invoice;
    }
  }

  /**
   * Get invoices for a specific user with cache fallback
   * @param userId User ID to filter invoices
   * @returns Array of invoices for the specified user
   */
  public async getInvoices(userId: string): Promise<any[]> {
    try {
      // Get all invoices first
      const allInvoices = await this.getAllInvoices();

      // Filter for this user and exclude deleted invoices unless specifically requested
      return allInvoices.filter(invoice =>
        invoice.userId === userId &&
        invoice.status !== 'deleted'
      );
    } catch (error) {
      console.error('Error getting invoices for user:', error);

      // Try to get from cache as fallback
      const cachedInvoices = this.getFromCache<any[]>(this.CACHE_INVOICES) || [];

      return cachedInvoices.filter(invoice =>
        invoice.userId === userId &&
        invoice.status !== 'deleted'
      );
    }
  }

  /**
   * Get inventory items for a user
   * @param userId User ID
   * @returns Array of inventory items
   */
  public async getInventoryItems(userId: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.INVENTORY_STORE], 'readonly');
      const store = transaction.objectStore(this.INVENTORY_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        // Filter items by userId
        const items = request.result.filter(item => item.userId === userId);
        resolve(items);
      };

      request.onerror = (event) => {
        console.error('Error getting inventory items:', event);
        reject(new Error('Failed to get inventory items'));
      };
    });
  }

  /**
   * Get clients for a user
   * @param userId User ID
   * @returns Array of clients
   */
  public async getClients(userId: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.CLIENTS_STORE], 'readonly');
      const store = transaction.objectStore(this.CLIENTS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        // Filter clients by userId
        const clients = request.result.filter(client => client.userId === userId);
        resolve(clients);
      };

      request.onerror = (event) => {
        console.error('Error getting clients:', event);
        reject(new Error('Failed to get clients'));
      };
    });
  }

  /**
   * Get business details for a user
   * @param userId User ID
   * @returns Business details or null if not found
   */
  public async getBusinessDetails(userId: string): Promise<any | null> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.BUSINESS_STORE], 'readonly');
      const store = transaction.objectStore(this.BUSINESS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        // Find business details for this user
        const businessDetails = request.result.find(business => business.userId === userId);
        resolve(businessDetails || null);
      };

      request.onerror = (event) => {
        console.error('Error getting business details:', event);
        reject(new Error('Failed to get business details'));
      };
    });
  }

  /**
   * Get all invoices from IndexedDB with fallback to cache
   * @returns Array of all invoices
   */
  public async getAllInvoices(): Promise<any[]> {
    // First try to get from IndexedDB
    try {
      if (!this.db) {
        const initialized = await this.initDB();
        if (!initialized) {
          const cachedInvoices = this.getFromCache<any[]>(this.CACHE_INVOICES) || [];
          return cachedInvoices;
        }
      }

      return new Promise((resolve, reject) => {
        if (!this.db) {
          // Fallback to cache if DB isn't available
          const cachedInvoices = this.getFromCache<any[]>(this.CACHE_INVOICES) || [];
          resolve(cachedInvoices);
          return;
        }

        const transaction = this.db.transaction([this.INVOICES_STORE], 'readonly');
        const store = transaction.objectStore(this.INVOICES_STORE);
        const request = store.getAll();

        request.onsuccess = () => {
          const invoices = request.result || [];
          // Also update cache for future offline use
          if (invoices.length > 0) {
            this.saveToCache(this.CACHE_INVOICES, invoices);
          }
          resolve(invoices);
        };

        request.onerror = (event) => {
          console.error('Error getting all invoices:', event);
          // Fallback to cache if there's an error
          const cachedInvoices = this.getFromCache<any[]>(this.CACHE_INVOICES) || [];
          resolve(cachedInvoices);
        };
      });
    } catch (error) {
      console.error('Error in getAllInvoices:', error);
      // Fallback to cache if there's an exception
      const cachedInvoices = this.getFromCache<any[]>(this.CACHE_INVOICES) || [];
      return cachedInvoices;
    }
  }

  /**
   * Save business details to the database
   * @param business Business details object to save
   * @returns Promise that resolves when save is complete
   */
  public async saveBusinessDetails(business: any): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      // Update sync status and timestamp
      business.syncStatus = 'pending';
      business.updatedAt = new Date().toISOString();

      const transaction = this.db.transaction([this.BUSINESS_STORE], 'readwrite');
      const store = transaction.objectStore(this.BUSINESS_STORE);
      const request = store.put(business);

      request.onsuccess = () => {
        this.notifyListeners();
        resolve();
      };

      request.onerror = (event) => {
        console.error('Error saving business details:', event);
        reject(new Error('Failed to save business details'));
      };
    });
  }

  /**
   * Save inventory item to the database
   * @param item Inventory item to save
   * @returns Promise that resolves when save is complete
   */
  public async saveInventoryItem(item: any): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      // Update sync status and timestamp
      item.syncStatus = 'pending';
      item.updatedAt = new Date().toISOString();
      if (!item.createdAt) {
        item.createdAt = item.updatedAt;
      }


      const transaction = this.db.transaction([this.INVENTORY_STORE], 'readwrite');
      const store = transaction.objectStore(this.INVENTORY_STORE);
      const request = store.put(item);

      request.onsuccess = () => {
        this.notifyListeners();
        resolve();
      };

      request.onerror = (event) => {
        console.error('Error saving inventory item:', event);
        reject(new Error('Failed to save inventory item'));
      };
    });
  }

  /**
   * Generic get item by ID
   */
  public async getItemById<T>(storeName: string, id: string): Promise<T | undefined> {
    return this.getItem<T>(storeName, id);
  }

  /**
   * Generic get item
   */
  public async getItem<T>(storeName: string, id: string): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        // rejection or try init
        this.initDB().then(success => {
          if (success && this.db) {
            this.getItem<T>(storeName, id).then(resolve).catch(reject);
          } else {
            resolve(undefined);
          }
        });
        return;
      }

      // Map common store names if necessary, or pass through
      const store = this.getStoreName(storeName);
      if (!this.db.objectStoreNames.contains(store)) {
        resolve(undefined);
        return;
      }

      const transaction = this.db.transaction([store], 'readonly');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.get(id);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        resolve(undefined);
      };
    });
  }

  /**
   * Generic add item
   */
  public async addItem(storeName: string, item: any): Promise<string> {
    return this.putItem(storeName, item);
  }

  /**
   * Generic update item
   */
  /**
   * Generic get all items
   */
  /**
   * Generic update item
   */
  public async updateItem(storeName: string, item: any): Promise<string> {
    return this.putItem(storeName, item);
  }

  /**
   * Generic put item
   */
  public async putItem(storeName: string, item: any): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const store = this.getStoreName(storeName);
      if (!this.db.objectStoreNames.contains(store)) {
        reject(new Error('Store ' + store + ' not found'));
        return;
      }

      // Ensure item has sync status if applicable
      if (typeof item === 'object' && item !== null) {
        if (!item.syncStatus) item.syncStatus = 'pending';
        if (!item.updatedAt) item.updatedAt = new Date().toISOString();
        if (!item.id) item.id = uuidv4();
      }

      const transaction = this.db.transaction([store], 'readwrite');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.put(item);

      request.onsuccess = () => {
        this.notifyListeners();
        resolve(item.id);
      };

      request.onerror = (event) => {
        console.error('Error putting item to ' + store + ': ', event);
        reject(new Error('Failed to save to ' + store));
      };
    });
  }


  /**
   * Generic delete item
   */
  public async deleteItem(storeName: string, id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const store = this.getStoreName(storeName);
      if (!this.db.objectStoreNames.contains(store)) {
        reject(new Error('Store ' + store + ' not found'));
        return;
      }

      const transaction = this.db.transaction([store], 'readwrite');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.delete(id);

      request.onsuccess = () => {
        this.notifyListeners();
        resolve();
      };

      request.onerror = (event) => {
        console.error('Error deleting item from ' + store + ': ', event);
        reject(new Error('Failed to delete from ' + store));
      };
    });
  }

  /**
   * Generic get all items
   */
  public async getAllItems<T>(storeName: string): Promise<T[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        this.initDB().then(success => {
          if (success && this.db) {
            this.getAllItems<T>(storeName).then(resolve).catch(reject);
          } else {
            resolve([]);
          }
        });
        return;
      }

      const store = this.getStoreName(storeName);
      if (!this.db.objectStoreNames.contains(store)) {
        resolve([]);
        return;
      }

      const transaction = this.db.transaction([store], 'readonly');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = (event) => {
        console.error('Error getting all items from ' + store + ': ', event);
        resolve([]);
      };
    });
  }

  private getStoreName(name: string): string {
    // Mapping for legacy names
    if (name === 'businesses') return 'business';
    if (name === 'businessProfiles') return 'business';
    return name;
  }

  /**
   * Save client to the database
   * @param client Client to save
   * @returns Promise that resolves when save is complete
   */
  public async saveClient(client: any): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      // Update sync status and timestamp
      client.syncStatus = 'pending';
      client.updatedAt = new Date().toISOString();
      if (!client.createdAt) {
        client.createdAt = client.updatedAt;
      }

      const transaction = this.db.transaction([this.CLIENTS_STORE], 'readwrite');
      const store = transaction.objectStore(this.CLIENTS_STORE);
      const request = store.put(client);

      request.onsuccess = () => {
        this.notifyListeners();
        resolve();
      };

      request.onerror = (event) => {
        console.error('Error saving client:', event);
        reject(new Error('Failed to save client'));
      };
    });
  }
}

export const indexedDBService = new IndexedDBService();
export default indexedDBService; 